import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";

const RANGE_INDEX_ATTR = "data-range-index";
const RANGE_IGNORE_SELECTOR = ".ant-select, button, input, textarea, a, .anticon";
const AUTO_SCROLL_EDGE = 48;
const AUTO_SCROLL_MAX_SPEED = 18;

interface RangeDrag {
  startIndex: number;
  lastIndex: number;
  mode: "add" | "remove";
  base: Set<number>;
  pointerY: number;
  raf: number | null;
  onMove: (e: MouseEvent) => void;
  onUp: () => void;
}

interface RangeAnchor {
  id: number;
  base: Set<number>;
}

/**
 *
 * @param orderedIds 화면 순서대로의 id 목록 (범위 계산용)
 */
export function useMediaMultiSelect(open: boolean, orderedIds: (number | undefined)[] = []) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [draggingId, setDraggingId] = useState<number | null>(null);
  /** 목록을 감싸는 스크롤 영역 (Shift+드래그 자동 스크롤·행 찾기 기준) */
  const scrollRef = useRef<HTMLDivElement>(null);
  const rangeRef = useRef<RangeDrag | null>(null);
  const orderedIdsRef = useRef(orderedIds);
  orderedIdsRef.current = orderedIds;
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const anchorRef = useRef<RangeAnchor | null>(null);

  const applyRange = (drag: RangeDrag, currentIndex: number) => {
    const from = Math.min(drag.startIndex, currentIndex);
    const to = Math.max(drag.startIndex, currentIndex);
    const next = new Set(drag.base);
    for (let i = from; i <= to; i++) {
      const id = orderedIdsRef.current[i];
      if (id == null) continue;
      if (drag.mode === "add") next.add(id);
      else next.delete(id);
    }
    drag.lastIndex = currentIndex;
    setSelectedIds(next);
  };

  /** 포인터 높이에 있는 행 인덱스 (스크롤 영역 밖이면 가장자리 행) */
  const rowIndexAt = (clientY: number): number | null => {
    const container = scrollRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    const y = Math.min(Math.max(clientY, rect.top + 2), rect.bottom - 2);
    const x = rect.left + rect.width / 2;
    const row = document.elementFromPoint(x, y)?.closest(`[${RANGE_INDEX_ATTR}]`);
    if (!row || !container.contains(row)) return null;
    const idx = Number(row.getAttribute(RANGE_INDEX_ATTR));
    return Number.isNaN(idx) ? null : idx;
  };

  const updateRangeFromPointer = () => {
    const drag = rangeRef.current;
    if (!drag) return;
    const idx = rowIndexAt(drag.pointerY);
    if (idx != null && idx !== drag.lastIndex) applyRange(drag, idx);
  };

  const autoScrollTick = () => {
    const drag = rangeRef.current;
    const container = scrollRef.current;
    if (!drag || !container) return;
    const rect = container.getBoundingClientRect();
    let delta = 0;
    if (drag.pointerY < rect.top + AUTO_SCROLL_EDGE) {
      delta = -Math.min(AUTO_SCROLL_MAX_SPEED, (rect.top + AUTO_SCROLL_EDGE - drag.pointerY) / 2);
    } else if (drag.pointerY > rect.bottom - AUTO_SCROLL_EDGE) {
      delta = Math.min(AUTO_SCROLL_MAX_SPEED, (drag.pointerY - (rect.bottom - AUTO_SCROLL_EDGE)) / 2);
    }
    if (delta !== 0) {
      container.scrollTop += delta;
      updateRangeFromPointer();
    }
    drag.raf = requestAnimationFrame(autoScrollTick);
  };

  const endRangeDrag = useCallback(() => {
    const drag = rangeRef.current;
    if (!drag) return;
    rangeRef.current = null;
    if (drag.raf != null) cancelAnimationFrame(drag.raf);
    window.removeEventListener("mousemove", drag.onMove);
    window.removeEventListener("mouseup", drag.onUp);
  }, []);

  /** 행의 onMouseDown 에 연결 */
  const handleRowMouseDown = (e: ReactMouseEvent, index: number) => {
    if (!e.shiftKey || e.button !== 0) return;
    if ((e.target as HTMLElement).closest(RANGE_IGNORE_SELECTOR)) return;
    const id = orderedIdsRef.current[index];
    if (id == null) return;

    // 텍스트 선택 방지
    e.preventDefault();
    endRangeDrag();

    const anchor = anchorRef.current;
    const anchorIndex = anchor ? orderedIdsRef.current.indexOf(anchor.id) : -1;
    const fromAnchor = anchor != null && anchorIndex !== -1;
    if (!fromAnchor) {
      anchorRef.current = { id, base: new Set(selectedIdsRef.current) };
    }

    const startIndex = fromAnchor ? anchorIndex : index;
    const drag: RangeDrag = {
      startIndex,
      lastIndex: startIndex,
      mode: fromAnchor || !selectedIdsRef.current.has(id) ? "add" : "remove",
      base: new Set(fromAnchor ? anchor!.base : selectedIdsRef.current),
      pointerY: e.clientY,
      raf: null,
      onMove: (ev: MouseEvent) => {
        drag.pointerY = ev.clientY;
        updateRangeFromPointer();
      },
      onUp: endRangeDrag,
    };
    rangeRef.current = drag;
    applyRange(drag, index);

    window.addEventListener("mousemove", drag.onMove);
    window.addEventListener("mouseup", drag.onUp);
    drag.raf = requestAnimationFrame(autoScrollTick);
  };

  /** 행에 펼쳐 넣는 속성 (범위 선택용 인덱스) */
  const rangeRowProps = (index: number) => ({ [RANGE_INDEX_ATTR]: index });

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setDraggingId(null);
      anchorRef.current = null;
    }
    return endRangeDrag;
  }, [open, endRangeDrag]);

  const toggle = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const clear = () => {
    anchorRef.current = null;
    setSelectedIds(new Set());
  };

  const handleRowClick = (e: ReactMouseEvent, id?: number) => {
    if (id == null) return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      e.stopPropagation();
      const next = new Set(selectedIdsRef.current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setSelectedIds(next);
      anchorRef.current = { id, base: next };
    }
  };

  const rowState = (id: number | undefined, isDragging: boolean) => {
    const isSelected = id != null && selectedIds.has(id);
    const isGroupDrag =
      draggingId != null && selectedIds.has(draggingId) && selectedIds.size > 1;
    return {
      isSelected,
      isGhosting: !!(isGroupDrag && isSelected && !isDragging),
      showBadge: !!(isDragging && isGroupDrag),
      badgeCount: selectedIds.size,
    };
  };

  return {
    selectedIds,
    setSelectedIds,
    toggle,
    clear,
    handleRowClick,
    handleRowMouseDown,
    rangeRowProps,
    scrollRef,
    draggingId,
    setDraggingId,
    rowState,
  };
}

export function multiSelectReorder<T>(
  items: T[],
  getId: (item: T) => number | undefined,
  selectedIds: number[],
  sourceIndex: number,
  destinationIndex: number
): T[] {
  const simpleMove = () => {
    const result = items.slice();
    const [moved] = result.splice(sourceIndex, 1);
    result.splice(destinationIndex, 0, moved);
    return result;
  };

  const dragged = items[sourceIndex];
  if (!dragged) return items.slice();
  const draggedId = getId(dragged);

  const selectedSet = new Set(
    selectedIds.filter((id) => items.some((it) => getId(it) === id))
  );

  // 단일 이동: 드래그한 항목이 선택 그룹에 없거나 선택이 1개 이하인 경우
  if (draggedId == null || !selectedSet.has(draggedId) || selectedSet.size <= 1) {
    return simpleMove();
  }

  const groupItems = items.filter((it) => {
    const id = getId(it);
    return id != null && selectedSet.has(id);
  });

  const moved = simpleMove();
  const withoutOthers = moved.filter((it) => {
    const id = getId(it);
    return id === draggedId || id == null || !selectedSet.has(id);
  });
  const draggedPos = withoutOthers.findIndex((it) => getId(it) === draggedId);
  const before = withoutOthers.slice(0, draggedPos);
  const after = withoutOthers.slice(draggedPos + 1);
  return [...before, ...groupItems, ...after];
}
